import {
    type ImAccount,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';

import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitConfig,
    type IncomeSplitRule,
    IncomeSplitRuleType,
    type PaycheckRoutingRuleAccountMetadata,
    PaycheckRoutingRuleAccountType,
    type ProposedAccount,
} from '../types/IncomeSplit';
import {
    getApexAccountDetails,
    hasApexCreateParameters,
    hasCashCreateParameters,
    type IncomeSplitRuleAccount,
    isBankAccountDisplayDetail,
} from './incomeSplitAccountUtils';

export const formatIncomeSplitPercentagesNew = (
    incomeSplit: IncomeSplitConfig,
): IncomeSplitConfig => {
    return {
        ...incomeSplit,
        ruleSets: incomeSplit.ruleSets.map((group) => ({
            ...group,
            rules: group.rules.map((rule) => {
                const { percentage } = rule;
                return {
                    ...rule,
                    percentage:
                        percentage != null
                            ? Math.round(percentage * 100)
                            : undefined,
                };
            }),
        })),
    };
};

export const hasConditionsNew = (rule: IncomeSplitRule): boolean => {
    const {
        type,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
        retirementContributionLimit,
        targetBalanceCents,
    } = rule;

    return (
        type === IncomeSplitRuleType.MINIMUM_BALANCE ||
        (targetBalanceCents ?? 0) > 0 ||
        (monthlyContributionMaxCents ?? 0) > 0 ||
        (yearlyContributionMaxCents ?? 0) > 0 ||
        (retirementContributionLimit ?? 0) > 0
    );
};

export const calculateFinalizedAmountNew = (rule: IncomeSplitRule): string => {
    return rule.percentage != null ? `${rule.percentage}%` : '';
};

interface GetAccountFromRuleNewParams {
    rule: IncomeSplitRule;
    bankAccountDetails: IncomeSplitRuleAccount[];
    externalAccounts: DepositAccount[];
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[];
    internalInvestmentAccounts?: ImAccount[];
}

const getUnderlyingMoneyMapAccount = (moneyMapRule: MoneyMapRuleWithAccount) =>
    moneyMapRule.moneyMapAccount.moneyMapAccountForTransfer ??
    moneyMapRule.moneyMapAccount;

const findMoneyMapRuleWithProposedAccount = (
    rule: IncomeSplitRule,
    moneyMapRulesWithProposedAccounts: MoneyMapRuleWithAccount[] = [],
) =>
    moneyMapRulesWithProposedAccounts.find((moneyMapRule) => {
        const underlyingAccount = getUnderlyingMoneyMapAccount(moneyMapRule);
        return (
            (underlyingAccount.type === MoneyMapAccountType.INTERNAL_BANKING ||
                underlyingAccount.type ===
                    MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) &&
            underlyingAccount.status === MoneyMapAccountStatus.PROPOSED &&
            moneyMapRule.id === rule.moneyMapRuleId
        );
    });

const getProposedAccountFromRule = (
    rule: IncomeSplitRule,
    moneyMapRulesWithProposedAccounts: MoneyMapRuleWithAccount[] = [],
): PaycheckRoutingRuleAccountMetadata | ProposedAccount | undefined => {
    const moneyMapRuleWithProposedAccount = findMoneyMapRuleWithProposedAccount(
        rule,
        moneyMapRulesWithProposedAccounts,
    );

    if (!moneyMapRuleWithProposedAccount) {
        return undefined;
    }

    const underlyingAccount = getUnderlyingMoneyMapAccount(
        moneyMapRuleWithProposedAccount,
    );

    if (
        underlyingAccount.type ===
        MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
    ) {
        const accountDetails = getApexAccountDetails(underlyingAccount);
        const { createParameters } = underlyingAccount;
        if (!hasApexCreateParameters(createParameters)) {
            return undefined;
        }

        return {
            nickname: accountDetails?.name ?? createParameters.name,
            icon: createParameters.icon,
            color: createParameters.color,
            accountNumber: accountDetails?.accountNumberMasked ?? null,
            accountType:
                accountDetails?.apexAccountType ??
                createParameters.apexAccountType,
        } satisfies PaycheckRoutingRuleAccountMetadata;
    }

    const { createParameters } = underlyingAccount;
    if (!hasCashCreateParameters(createParameters)) {
        return undefined;
    }

    const { name, icon, color } = createParameters;
    return {
        nickname: name,
        icon,
        color,
    } satisfies ProposedAccount;
};

export const getAccountFromRuleNew = ({
    rule,
    bankAccountDetails,
    externalAccounts,
    moneyMapRulesWithProposedAccounts = [],
    internalInvestmentAccounts = [],
}: GetAccountFromRuleNewParams):
    | PaycheckRoutingRuleAccountMetadata
    | BankAccountDisplayDetail
    | DepositAccount
    | ProposedAccount
    | undefined => {
    const proposedAccount = getProposedAccountFromRule(
        rule,
        moneyMapRulesWithProposedAccounts,
    );
    if (proposedAccount) {
        return proposedAccount;
    }

    if (rule.accountMetadata) {
        return rule.accountMetadata;
    }

    const foundInBankAccounts = bankAccountDetails.find(
        (account) =>
            isBankAccountDisplayDetail(account) &&
            account.bankAccountId === rule.accountId,
    );
    const foundInExternalAccounts = externalAccounts.find(
        (account) => account.id === rule.accountId,
    );
    const foundInternalInvestmentAccount =
        rule.accountType === PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
            ? internalInvestmentAccounts.find(
                  (account) => account.apexAccountId === rule.accountId,
              )
            : undefined;

    const foundInInternalInvestmentAccounts = foundInternalInvestmentAccount
        ? ({
              nickname: foundInternalInvestmentAccount.nickname,
              icon: foundInternalInvestmentAccount.icon,
              color: foundInternalInvestmentAccount.color,
              accountNumber: foundInternalInvestmentAccount.accountNumber,
              accountType: foundInternalInvestmentAccount.registrationType,
          } satisfies PaycheckRoutingRuleAccountMetadata)
        : undefined;

    const foundAccount =
        foundInBankAccounts ??
        foundInExternalAccounts ??
        foundInInternalInvestmentAccounts;

    if (!foundAccount && rule.accountId) {
        if (rule.accountType === PaycheckRoutingRuleAccountType.EXTERNAL) {
            return {
                id: rule.accountId,
                nickname: 'Closed Account',
                isExternal: true,
            } satisfies DepositAccount;
        }

        if (
            rule.accountType ===
            PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
        ) {
            return {
                nickname: 'Investment Account',
                icon: null,
                color: null,
            } satisfies PaycheckRoutingRuleAccountMetadata;
        }
    }

    return foundAccount;
};

export const isBackdoorTradRule = ({
    rule,
    internalInvestmentAccounts = [],
    moneyMapRulesWithProposedAccounts = [],
}: {
    rule: IncomeSplitRule;
    internalInvestmentAccounts?: ImAccount[];
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[];
}): boolean => {
    if (
        rule.accountType === PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
    ) {
        const existingMatch = internalInvestmentAccounts.some(
            (im) =>
                im.apexAccountId === rule.accountId &&
                im.backdoorRothRole === 'TRADITIONAL' &&
                im.autoConvert === true,
        );
        if (existingMatch) return true;
    }

    const moneyMapRule = findMoneyMapRuleWithProposedAccount(
        rule,
        moneyMapRulesWithProposedAccounts,
    );
    if (moneyMapRule != null) {
        const underlying = getUnderlyingMoneyMapAccount(moneyMapRule);
        if (
            underlying.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
            underlying.backdoorRothLinkedMoneyMapAccountId != null
        ) {
            return true;
        }
    }

    return false;
};

/*
 * Creates a sort function that sorts income split rules for display according
 * to their underlying money map types.  Bills -> Spend -> Goals -> Other
 */
export const sortIncomeSplitRules = (
    moneyMap?: MoneyMapWithRulesAndAccounts,
) => {
    return (a: IncomeSplitRule, b: IncomeSplitRule) => {
        const getTypeSortValue = (t: MoneyMapRuleType) => {
            switch (t) {
                case MoneyMapRuleType.BILLS:
                    return 1;
                case MoneyMapRuleType.SPEND:
                    return 2;
                case MoneyMapRuleType.GOAL:
                    return 3;
                case MoneyMapRuleType.ACCOUNT_ONLY:
                    return 4;
            }
        };

        const mmRuleA = moneyMap?.moneyMapRules.find(
            (r) => r.id === a.moneyMapRuleId,
        );
        const mmRuleB = moneyMap?.moneyMapRules.find(
            (r) => r.id === b.moneyMapRuleId,
        );
        if (mmRuleA && mmRuleB) {
            // Sort same types by percentage, descending
            if (mmRuleA.type === mmRuleB.type) {
                return (b.percentage ?? 0) - (a.percentage ?? 0);
            }

            return (
                getTypeSortValue(mmRuleA.type) - getTypeSortValue(mmRuleB.type)
            );
        }

        // Fallback to percentage
        return (b.percentage ?? 0) - (a.percentage ?? 0);
    };
};
